import { useEffect, useMemo, useState } from 'react'

import { FEEDBACK_STATUS } from 'constants/feedback'
import { useAuthorizationContext, useClientContext } from 'contexts'
import { useGlobalContext } from 'contexts/global'
import { isEmpty } from 'lodash'
import { useGetAuthorization } from 'services/auth'
import { TError } from 'types'

function useIntro() {
  const [errorData, setErrorData] = useState<TError>()

  const {
    authorizationId,
    setAuthorizationId,
    setConsentRequested,
    setRequestedClaims,
    setRequestedScopes,
  } = useAuthorizationContext()

  const { setInterIDContent } = useGlobalContext()

  const { setClient } = useClientContext()

  const { client, consentRequested, redirectUri, isError, isLoading, error } = useGetAuthorization({
    id: authorizationId,
  })

  const isLoadingPage = useMemo(() => isLoading || isEmpty(client), [isLoading, client])

  useEffect(() => {
    if (authorizationId) return

    const urlParams = new URLSearchParams(window.location.search)
    const id = urlParams.get('authorization_id')

    if (id) {
      setAuthorizationId(id)
    } else {
      setErrorData({
        scenario: 'unidentified',
        step: 'access_denied',
        error_description: 'nonexistent authorization_id.',
        error_message: 'N/A - Exits the WebView.',
      })
    }
  }, [authorizationId, setAuthorizationId])

  useEffect(() => {
    if (!isEmpty(client) && !isEmpty(consentRequested)) {
      const requestedClains = consentRequested.userinfo.map((item) => item.claim)
      setClient(client)
      setConsentRequested(consentRequested)
      setRequestedClaims(requestedClains)
      setRequestedScopes(consentRequested.accessScope?.map((item) => item.scope) ?? [])
    }
  }, [client, consentRequested])

  useEffect(() => {
    if (!isLoadingPage) setInterIDContent()
  }, [isLoadingPage])

  useEffect(() => {
    if (isError) {
      setErrorData({
        scenario: 'unidentified',
        step: 'access_denied',
        error_description: JSON.stringify(error),
        error_message: FEEDBACK_STATUS.error.title,
      })
    }
  }, [isError])

  return {
    client,
    consentRequested,
    redirectUri,
    isError,
    isLoading: isLoadingPage,
    error,
    errorData,
    setErrorData,
  }
}

export { useIntro }
